import { Chart, ChartData, Circle, HelpText } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { useBotAnalyticsCRUDActions } from '@mm/state/actions/BotAnalyticsCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  selectContactsAmountAnalytics,
  selectContactsByPlatform,
} from '@mm/state/selectors/botAnalyticsSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import classnames from 'classnames';
import { sum } from 'lodash';
import { Moment } from 'moment';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { colors } from '../../../../../styles/colors';
import { getDormatedDateLabel } from '../../../../../utils/dateUtils';
import './ContactsByChart.scss';

type Props = {
  startDate: Moment | null;
  endDate: Moment | null;
  dateRangeMode: string | null;
};

export const ContactsByChart = (props: Props) => {
  const { startDate, endDate, dateRangeMode } = props;
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const contactsByPlatform = useSelector(selectContactsByPlatform);
  const contactsAmountAnalytics = useSelector(selectContactsAmountAnalytics);
  const { openUniversalUpgradeLandmineModal } = useModalActions();
  const { totalNewContacts, sessions, date, messagesCount } =
    contactsAmountAnalytics;
  const { facebook, webChat, sms, instagram = [], dates } = contactsByPlatform;
  const [isContactsByPlatformMode, setIsContactsByPlatformMode] =
    useState(false);
  const { retrieveContactsByType, retrieveContactsAmountAnalytics } =
    useBotAnalyticsCRUDActions();

  const canViewContactsByPlatform = useSelector(
    combinedFeatureFlagsSelector,
  ).analyticsBotContactsByPlatform;

  const totalsByPlatform = [
    sum(facebook),
    sum(webChat),
    sum(sms),
    sum(instagram),
  ];
  const msgsPerConversation = messagesCount.map((messageCount, index) => {
    return messageCount && sessions[index]
      ? Math.round(messageCount / sessions[index])
      : 0;
  });

  const avgUserMsgsPerConversation =
    sum(msgsPerConversation) / msgsPerConversation.length;

  useEffect(() => {
    retrieveContactsByType(
      activeChatbotId,
      'by_platform',
      startDate?.format('YYYY-MM-DD'),
      endDate?.format('YYYY-MM-DD'),
    );
    retrieveContactsAmountAnalytics(
      activeChatbotId,
      startDate?.format('YYYY-MM-DD'),
      endDate?.format('YYYY-MM-DD'),
    );
  }, [startDate, endDate]);

  const contactsConversationsData = {
    labels: getDormatedDateLabel(date, 'day'),
    data: [totalNewContacts, sessions],
    color: [colors.blue, colors.orange],
    title: ['Contacts Created', 'Conversation Sessions'],
  };

  const contactsByPlatformData: ChartData = {
    labels: getDormatedDateLabel(dates, 'day'),
    data: [facebook, webChat, sms, instagram],
    color: [colors.blue, colors.bluePurple, colors.orange, colors.redLight],
    title: ['FB Messenger', 'Webchat', 'SMS', 'Instagram'],
    types: ['bar', 'bar', 'bar', 'bar'],
    withoutGradient: true,
  };

  const chartData = isContactsByPlatformMode
    ? contactsByPlatformData
    : contactsConversationsData;

  const tooltipsCallbacks = {
    afterBody: (
      c: {
        index: number;
      }[],
    ) => {
      const { index } = c[0];
      return isContactsByPlatformMode
        ? ''
        : `   Avg user msgs per conversation: ${msgsPerConversation[index]}`;
    },
  };
  const additionalOpts = {
    stacked: isContactsByPlatformMode,
  };

  const onSetContactsByPlatformMode = () => {
    if (!canViewContactsByPlatform) {
      openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['BOT-ANALYTICS-CHART-TAB'],
        feature: 'analyticsBotContactsByPlatform',
      });
    } else {
      setIsContactsByPlatformMode(true);
    }
  };

  const toggleContactsPerDay = classnames(
    'contacts-by-chart__header-title-toggle',
    {
      'contacts-by-chart__header-title-toggle--active':
        !isContactsByPlatformMode,
    },
  );
  const toggleContactsByPlatform = classnames(
    'contacts-by-chart__header-title-toggle',
    {
      'contacts-by-chart__header-title-toggle--active':
        isContactsByPlatformMode,
    },
  );

  const customLegend = (
    <>
      <div className="contacts-by-chart__legend">
        {chartData?.title?.map((title, i) => (
          <div className="c-chart__legend-item-container" key={title}>
            <div className="c-chart__legend-item">
              <Circle
                className="c-chart__legend-indicator"
                color={chartData?.color[i]}
              />
              <HelpText className="c-chart__legend-label" text={title} />
            </div>
            <div className="c-chart__legend-item-amount">
              {isContactsByPlatformMode
                ? totalsByPlatform[i]
                : sum(chartData.data[i])}
            </div>
          </div>
        ))}
      </div>
      <div className="contacts-by-chart__legend-info">
        <div className="contacts-by-chart__legend-date">
          <span className="contacts-by-chart__legend-date-show">Show: </span>
          {dateRangeMode ? dateRangeMode : 'Custom date range'}
        </div>
        {!isContactsByPlatformMode && (
          <div className="contacts-by-chart__avg-msgs">
            Avg user msgs per conversation:{' '}
            <span className="contacts-by-chart__avg-msgs-count">
              {avgUserMsgsPerConversation.toFixed(2)}
            </span>
          </div>
        )}
      </div>
    </>
  );
  return (
    <div className="contacts-by-chart">
      <div className="contacts-by-chart__header">
        <div
          className={toggleContactsPerDay}
          onClick={() => setIsContactsByPlatformMode(false)}
        >
          Daily Contacts/Sessions
        </div>
        <div
          className={toggleContactsByPlatform}
          onClick={onSetContactsByPlatformMode}
        >
          Daily Contacts by Platform
        </div>
      </div>
      <Chart
        className="contacts-by__chart-main"
        data={chartData}
        customLegend={customLegend}
        additionalOpts={additionalOpts}
        tooltipsCallbacks={tooltipsCallbacks}
      />
    </div>
  );
};
